import { Link } from '@tanstack/react-router';
import { createContext, Fragment, useContext, type ReactNode } from 'react';

import { ArticleCardLink } from '@/components/site/article-card-link';
import { FadeCoverGallery } from '@/components/site/fade-cover-gallery';
import { HomeHScrollTrack } from '@/components/site/home-h-scroll';
import { LinkedCardCover, cardCoverSrcs } from '@/components/site/linked-card-cover';
import { SoftImage } from '@/components/site/soft-image';
import { Cross } from '@/components/site/shell';
import { toCard } from '@/lib/article-card';
import { formatHebrewDate, getReadingTime } from '@/lib/timeUtils';
import { cn } from '@/lib/utils';

type CardArticle = ReturnType<typeof toCard>;

/** When true, {@link CompactMeta} shows each article's primary category. */
const ShowCategoryInMetaContext = createContext(false);

function ArticleTrailer({
    trailer,
    className,
}: {
    trailer?: string;
    className?: string;
}) {
    if (!trailer) return null;
    return (
        <p className={cn('text-sm tracking-wide text-brand-accent', className)}>
            {trailer}
        </p>
    );
}

function CardThumbLink({
    article,
    className,
    children,
}: {
    article: CardArticle;
    className?: string;
    children: ReactNode;
}) {
    return (
        <ArticleCardLink article={article} className={className}>
            <span className="sr-only">{article.title}</span>
            {children}
        </ArticleCardLink>
    );
}

export function ArticleRow({
    article,
    showBorder = true,
}: {
    article: CardArticle;
    showBorder?: boolean;
}) {
    const reading = getReadingTime(article.description ?? '').minutes;
    const hasCover = cardCoverSrcs(article).length > 0;

    const meta: { key: string; node: ReactNode }[] = [];
    if (article.createdAt) {
        meta.push({
            key: 'date',
            node: (
                <time dateTime={article.createdAt}>
                    {formatHebrewDate(article.createdAt).split(',')[0]}
                </time>
            ),
        });
    }
    meta.push({
        key: 'reading',
        node: <span>{reading} דק׳ קריאה</span>,
    });
    if (article.author) {
        meta.push({
            key: 'author',
            node: <span>{article.author}</span>,
        });
    }
    meta.push({
        key: 'read',
        node: (
            <ArticleCardLink
                article={article}
                className="hover:text-white"
            >
                לקריאה ←
            </ArticleCardLink>
        ),
    });

    return (
        <article
            className={cn(
                'flex items-start gap-4 px-6 py-8 md:gap-6',
                showBorder && 'border-b border-white/10',
            )}
        >
            {hasCover ? (
                <LinkedCardCover
                    article={article}
                    className="w-[104px] shrink-0 border border-white/10 sm:w-[132px] md:w-[180px]"
                    imageClassName="aspect-[4/3] w-full object-cover"
                    sizes="(min-width: 768px) 180px, 132px"
                    loading="lazy"
                    showControls={false}
                />
            ) : null}
            <div className="min-w-0 flex-1">
                <ArticleTrailer trailer={article.trailer} />
                {article.isLiveReport && (
                    <p className={cn('text-sm text-brand-accent', article.trailer && 'mt-1')}>
                        שידור חי
                    </p>
                )}
                <h2
                    className={cn(
                        'title-single-line text-xl font-normal tracking-tight md:text-2xl',
                        (article.trailer || article.isLiveReport) && 'mt-2',
                    )}
                >
                    <ArticleCardLink
                        article={article}
                        className="text-white hover:text-white/85"
                    >
                        {article.title}
                    </ArticleCardLink>
                </h2>
                {article.description && (
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/55 md:text-[15px]">
                        <ArticleCardLink
                            article={article}
                            className="hover:text-white/70"
                        >
                            {article.description}
                        </ArticleCardLink>
                    </p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-white/55">
                    {meta.map((item, index) => (
                        <Fragment key={item.key}>
                            {index > 0 && (
                                <span className="text-white/25" aria-hidden>
                                    ·
                                </span>
                            )}
                            {item.node}
                        </Fragment>
                    ))}
                </div>
            </div>
        </article>
    );
}

export function FeaturedHero({
    article,
    fullWidth = false,
}: {
    article: CardArticle;
    /** When true, skip sidebar-height lock and use natural cover aspect. */
    fullWidth?: boolean;
}) {
    const hasCover = cardCoverSrcs(article).length > 0;

    return (
        <div className="relative">
            <div className="grid-dots" aria-hidden />
            <div className="relative px-6 pt-6 pb-8 md:pb-10">
                {hasCover && (
                    <LinkedCardCover
                        article={article}
                        className={cn(
                            'image-frame--photo mb-8 border border-white/10',
                            !fullWidth && 'lg:h-[var(--homepage-tall-ad-h)]',
                        )}
                        width={1600}
                        height={900}
                        sizes={fullWidth ? '100vw' : '(min-width: 1024px) 66vw, 100vw'}
                        imageClassName={
                            fullWidth
                                ? 'aspect-[16/9] h-full w-full'
                                : 'aspect-[16/9] h-full w-full lg:aspect-auto'
                        }
                        fetchPriority="high"
                        loading="eager"
                    />
                )}
                <h1 className="text-sm font-normal text-white/55">חדשות אשקלון</h1>
                <ArticleTrailer trailer={article.trailer} className="mt-2" />
                {article.isLiveReport && (
                    <p className="mt-2 text-sm text-brand-accent">שידור חי</p>
                )}
                <h2 className="mt-3 max-w-3xl text-3xl font-normal leading-tight tracking-[-0.03em] md:text-5xl">
                    <ArticleCardLink
                        article={article}
                        className="text-white hover:text-white/85"
                    >
                        {article.title}
                    </ArticleCardLink>
                </h2>
                {article.description && (
                    <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/55 md:text-lg">
                        <ArticleCardLink
                            article={article}
                            className="hover:text-white/70"
                        >
                            {article.description}
                        </ArticleCardLink>
                    </p>
                )}
                <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/55">
                    {article.author && <span>{article.author}</span>}
                    {article.createdAt && (
                        <time dateTime={article.createdAt}>
                            {formatHebrewDate(article.createdAt).split(',')[0]}
                        </time>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Full-viewport "רגע בתמונה" band - random recent gallery cover, edge to edge.
 * Caption bar re-aligns to the max-w-7xl grid below the photo.
 * Display only - no link to the source article.
 */
export function PictureMoment({ article }: { article: CardArticle }) {
    const covers = cardCoverSrcs(article);
    if (covers.length === 0) return null;

    return (
        <section className="relative w-full" aria-label="רגע בתמונה">
            <div className="image-frame image-frame--photo border-y border-white/10 py-6">
                <FadeCoverGallery
                    slides={covers.map((src, i) => ({
                        id: `${article.slug}-${i}`,
                        src,
                        alt: i === 0 ? article.title : '',
                    }))}
                    width={1600}
                    height={686}
                    sizes="100vw"
                    fetchPriority="high"
                    loading="eager"
                    imageClassName="aspect-[21/9] max-h-[min(78vh,52rem)] w-full"
                />
            </div>
            <div className="relative mx-auto w-full max-w-7xl border-x border-b border-white/10 px-6 py-6 md:py-8">
                {/* Match Cell seam marks: band sits outside Cell, so own bottom Crosses. */}
                <Cross className="-bottom-2.5 -start-[5px]" />
                <Cross className="-bottom-2.5 -end-[5px]" />
                <p className="text-sm text-white/55">רגע בתמונה</p>
                <ArticleTrailer trailer={article.trailer} className="mt-2" />
                <h2
                    className={cn(
                        'title-two-line max-w-3xl text-2xl font-normal tracking-[-0.03em] text-white md:text-3xl',
                        article.trailer ? 'mt-1' : 'mt-2',
                    )}
                >
                    {article.title}
                </h2>
                {article.description && (
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/55 md:text-[15px]">
                        {article.description}
                    </p>
                )}
            </div>
        </section>
    );
}

export type WeeklyRankedItem = {
    article: CardArticle;
    views: number;
};

/**
 * Horizontal most-read ranking under "רגע בתמונה".
 * Same track as מבזקים: hidden scrollbar, edge fade, prev/next off Apple.
 */
export function WeeklyTopRanking({
    items,
    connected = false,
    className,
}: {
    items: WeeklyRankedItem[];
    /** True when this band sits flush under PictureMoment (no extra top border). */
    connected?: boolean;
    className?: string;
}) {
    if (items.length === 0) return null;
    const maxViews = Math.max(...items.map((item) => item.views), 1);

    return (
        <section
            className={cn(
                'relative mx-auto w-full max-w-7xl border-x border-b border-white/10',
                !connected && 'border-t',
                className,
            )}
            aria-label="הכתבות הנקראות ביותר השבוע"
        >
            <Cross className="-bottom-2.5 -start-[5px]" />
            <Cross className="-bottom-2.5 -end-[5px]" />
            <div className="flex items-start gap-3 px-4 py-4 sm:gap-4 sm:px-6 sm:py-5">
                <div className="shrink-0 pt-1">
                    <p className="text-sm text-white/55">דירוג השבוע</p>
                    <h2 className="mt-1 text-base font-normal tracking-tight text-white">
                        הנקראות ביותר
                    </h2>
                </div>
                <HomeHScrollTrack
                    className="min-w-0 flex-1 self-stretch"
                    prevLabel="דירוג קודם"
                    nextLabel="דירוג הבא"
                    resetKey={items.map((item) => item.article.slug).join(',')}
                >
                    <ol className="flex w-max list-none items-stretch">
                        {items.map((item, index) => {
                            const { article } = item;
                            const rank = String(index + 1).padStart(2, '0');
                            const share = Math.max(item.views / maxViews, 0);
                            const hasCover = cardCoverSrcs(article).length > 0;
                            const isLast = index === items.length - 1;

                            return (
                                <li
                                    key={article.slug}
                                    className={cn(
                                        'flex w-[min(72vw,16.5rem)] shrink-0 flex-col px-4 py-1',
                                        !isLast && 'border-e border-white/10',
                                    )}
                                >
                                    <div className="flex items-baseline gap-3">
                                        <span
                                            className="font-mono text-xl tabular-nums tracking-tight text-white/55"
                                            aria-hidden
                                        >
                                            {rank}
                                        </span>
                                        {article.isLiveReport && (
                                            <p className="text-sm text-brand-accent">שידור חי</p>
                                        )}
                                    </div>
                                    {hasCover ? (
                                        <LinkedCardCover
                                            article={article}
                                            className="mt-3 border border-white/10"
                                            imageClassName="aspect-[16/10] w-full"
                                            width={396}
                                            height={248}
                                            sizes="(min-width: 640px) 16.5rem, 72vw"
                                            loading="lazy"
                                            showControls={false}
                                        />
                                    ) : (
                                        <div
                                            className="mt-3 aspect-[16/10] border border-white/10 bg-white/[0.03]"
                                            aria-hidden
                                        />
                                    )}
                                    <ArticleTrailer trailer={article.trailer} className="mt-3" />
                                    <h3
                                        className={cn(
                                            'title-two-line text-base font-normal leading-snug tracking-tight',
                                            article.trailer ? 'mt-1' : 'mt-3',
                                        )}
                                    >
                                        <ArticleCardLink
                                            article={article}
                                            className="text-white hover:text-white/85"
                                        >
                                            {article.title}
                                        </ArticleCardLink>
                                    </h3>
                                    <CompactMeta article={article} />
                                    <div
                                        className="mt-auto pt-4"
                                        aria-hidden
                                    >
                                        <div className="h-px w-full bg-white/[0.06]">
                                            <div
                                                className="h-px bg-white/35"
                                                style={{
                                                    width: `${Math.round(share * 100)}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </HomeHScrollTrack>
            </div>
        </section>
    );
}

/** Homepage category-rail layouts - rotate so consecutive sections don't match. */
export type CategoryRailVariant =
    | 'spotlight'
    | 'dual'
    | 'mosaic'
    | 'grid'
    | 'thumbs'
    | 'board'
    | 'feature'
    | 'docket';

function SectionHeader({
    title,
    label,
    categoryKey,
}: {
    title: string;
    label?: string;
    categoryKey?: string;
}) {
    return (
        <div className="flex items-end justify-between gap-4 border-b border-white/10 px-6 py-3">
            <div className="min-w-0">
                {label && (
                    <p className="text-xs tracking-wide text-white/55">{label}</p>
                )}
                <h2 className="title-single-line mt-1 text-base font-normal tracking-tight text-white md:text-lg">
                    {title}
                </h2>
            </div>
            {categoryKey && (
                <Link
                    to="/category/$key"
                    params={{ key: categoryKey }}
                    className="shrink-0 text-sm text-white/55 hover:text-white"
                >
                    למדור ←
                </Link>
            )}
        </div>
    );
}

function CompactMeta({ article }: { article: CardArticle }) {
    const showCategory = useContext(ShowCategoryInMetaContext);

    const items: { key: string; node: ReactNode }[] = [];

    if (article.isLiveReport) {
        items.push({
            key: 'live',
            node: <span className="text-brand-accent">שידור חי</span>,
        });
    }
    if (showCategory && article.categoryName) {
        items.push({
            key: 'category',
            node: article.categoryKey ? (
                <Link
                    to="/category/$key"
                    params={{ key: article.categoryKey }}
                    className="hover:text-white"
                >
                    {article.categoryName}
                </Link>
            ) : (
                <span>{article.categoryName}</span>
            ),
        });
    }
    if (article.createdAt) {
        items.push({
            key: 'date',
            node: (
                <time dateTime={article.createdAt}>
                    {formatHebrewDate(article.createdAt).split(',')[0]}
                </time>
            ),
        });
    }
    if (article.author) {
        items.push({
            key: 'author',
            node: <span>{article.author}</span>,
        });
    }

    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-white/55">
            {items.map((item, index) => (
                <Fragment key={item.key}>
                    {index > 0 && (
                        <span className="text-white/25" aria-hidden>
                            ·
                        </span>
                    )}
                    {item.node}
                </Fragment>
            ))}
        </div>
    );
}

function LeadTeaser({
    article,
    className,
}: {
    article: CardArticle;
    className?: string;
}) {
    const hasCover = cardCoverSrcs(article).length > 0;

    return (
        <article className={cn('group p-4 md:p-5', className)}>
            {hasCover ? (
                <LinkedCardCover
                    article={article}
                    className="mb-3 border border-white/10"
                    imageClassName="aspect-[16/10] w-full"
                    sizes="(min-width: 768px) 55vw, 100vw"
                    loading="lazy"
                />
            ) : null}
            <ArticleTrailer trailer={article.trailer} />
            {article.isLiveReport && (
                <p className={cn('text-sm text-brand-accent', article.trailer && 'mt-1')}>
                    שידור חי
                </p>
            )}
            <h3
                className={cn(
                    'title-two-line text-lg font-normal tracking-tight md:text-xl',
                    (article.trailer || article.isLiveReport) && 'mt-1',
                )}
            >
                <ArticleCardLink
                    article={article}
                    className="text-white hover:text-white/85"
                >
                    {article.title}
                </ArticleCardLink>
            </h3>
            {article.description && (
                <ArticleCardLink
                    article={article}
                    className="mt-2 line-clamp-2 block text-sm leading-relaxed text-white/55 hover:text-white/70"
                >
                    {article.description}
                </ArticleCardLink>
            )}
            <CompactMeta article={article} />
        </article>
    );
}

/** Cover + copy in a horizontal row - larger than compact list thumbs. */
function HorizontalLeadTeaser({
    article,
    className,
}: {
    article: CardArticle;
    className?: string;
}) {
    const hasCover = cardCoverSrcs(article).length > 0;

    return (
        <article
            className={cn(
                'group flex items-start gap-3 p-4 md:gap-5 md:p-5',
                className,
            )}
        >
            {hasCover ? (
                <LinkedCardCover
                    article={article}
                    className="w-[96px] shrink-0 border border-white/10 md:w-[42%] md:max-w-[280px]"
                    imageClassName="aspect-[4/3] w-full object-cover md:aspect-[16/10]"
                    sizes="(min-width: 768px) 280px, 96px"
                    loading="lazy"
                />
            ) : null}
            <div className="min-w-0 flex-1 self-center">
                <ArticleTrailer trailer={article.trailer} />
                {article.isLiveReport && (
                    <p className={cn('text-sm text-brand-accent', article.trailer && 'mt-1')}>
                        שידור חי
                    </p>
                )}
                <h3
                    className={cn(
                        'title-two-line text-base font-normal leading-snug tracking-tight md:text-xl',
                        (article.trailer || article.isLiveReport) && 'mt-1',
                    )}
                >
                    <ArticleCardLink
                        article={article}
                        className="text-white hover:text-white/85"
                    >
                        {article.title}
                    </ArticleCardLink>
                </h3>
                {article.description && (
                    <ArticleCardLink
                        article={article}
                        className="mt-1.5 line-clamp-2 hidden text-sm leading-relaxed text-white/55 hover:text-white/70 md:mt-2 md:block md:line-clamp-3"
                    >
                        {article.description}
                    </ArticleCardLink>
                )}
                <CompactMeta article={article} />
            </div>
        </article>
    );
}

function CompactTeaserList({ articles }: { articles: CardArticle[] }) {
    if (articles.length === 0) return null;

    return (
        <ul className="flex flex-col">
            {articles.map((article, index) => (
                <li
                    key={article.slug}
                    className={cn(
                        'group flex items-start gap-3 px-4 py-3 md:gap-4 md:px-5 md:py-5',
                        index < articles.length - 1 && 'border-b border-white/10',
                    )}
                >
                    {article.cover ? (
                        <CardThumbLink
                            article={article}
                            className="image-frame shrink-0 border border-white/10"
                        >
                            <SoftImage
                                src={article.cover}
                                alt=""
                                width={112}
                                height={84}
                                sizes="(min-width: 768px) 112px, 80px"
                                className="h-16 w-20 object-cover md:h-[84px] md:w-[112px]"
                                loading="lazy"
                            />
                        </CardThumbLink>
                    ) : null}
                    <div className="min-w-0 flex-1 self-center">
                        <ArticleTrailer trailer={article.trailer} />
                        <h3
                            className={cn(
                                'title-two-line text-base font-normal leading-snug tracking-tight md:text-lg',
                                article.trailer && 'mt-1',
                            )}
                        >
                            <ArticleCardLink
                                article={article}
                                className="text-white hover:text-white/85"
                            >
                                {article.title}
                            </ArticleCardLink>
                        </h3>
                        <CompactMeta article={article} />
                    </div>
                </li>
            ))}
        </ul>
    );
}

/** (A) Lead story with cover + secondary stack beside it. */
function SpotlightRail({ articles }: { articles: CardArticle[] }) {
    const [lead, ...rest] = articles;

    return (
        <div className="md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] md:items-stretch">
            <LeadTeaser
                article={lead}
                className="border-b border-white/10 md:border-b-0 md:border-e"
            />
            <CompactTeaserList articles={rest} />
        </div>
    );
}

/**
 * (A2) Two larger horizontal stories stacked + compact grid below.
 * Used for the homepage featured secondary block (6 items after the hero).
 */
function DualSpotlightRail({ articles }: { articles: CardArticle[] }) {
    const leads = articles.slice(0, 2);
    const rest = articles.slice(2);

    if (leads.length < 2) {
        return <SpotlightRail articles={articles} />;
    }

    return (
        <div>
            <div className="flex flex-col">
                {leads.map((article, index) => (
                    <HorizontalLeadTeaser
                        key={article.slug}
                        article={article}
                        className={cn(
                            (index < leads.length - 1 || rest.length > 0) &&
                                'border-b border-white/10',
                        )}
                    />
                ))}
            </div>
            {rest.length > 0 ? <GridRail articles={rest} /> : null}
        </div>
    );
}

/** Cover-forward tile: image on top, title below. */
function CoverTile({
    article,
    className,
    dense = false,
}: {
    article: CardArticle;
    className?: string;
    /** Tighter padding and shorter cover - used for mosaic side stack. */
    dense?: boolean;
}) {
    const hasCover = cardCoverSrcs(article).length > 0;

    return (
        <article
            className={cn(
                'group',
                dense ? 'p-3 md:p-3.5' : 'p-4 md:p-5',
                className,
            )}
        >
            {hasCover ? (
                <LinkedCardCover
                    article={article}
                    className={cn(
                        'border border-white/10',
                        dense ? 'mb-2' : 'mb-3',
                    )}
                    imageClassName={cn(
                        'w-full',
                        dense ? 'aspect-[2/1]' : 'aspect-[16/10]',
                    )}
                    sizes={dense ? '(min-width: 768px) 28vw, 100vw' : '(min-width: 640px) 33vw, 100vw'}
                    loading="lazy"
                />
            ) : (
                <div
                    className={cn(
                        'border border-white/10 bg-white/[0.03]',
                        dense ? 'mb-2 aspect-[2/1]' : 'mb-3 aspect-[16/10]',
                    )}
                    aria-hidden
                />
            )}
            <ArticleTrailer trailer={article.trailer} />
            {article.isLiveReport && (
                <p className={cn('text-sm text-brand-accent', article.trailer && 'mt-1')}>
                    שידור חי
                </p>
            )}
            <h3
                className={cn(
                    'title-two-line font-normal leading-snug tracking-tight',
                    dense ? 'text-sm md:text-base' : 'text-base md:text-lg',
                    (article.trailer || article.isLiveReport) && 'mt-1',
                )}
            >
                <ArticleCardLink
                    article={article}
                    className="text-white hover:text-white/85"
                >
                    {article.title}
                </ArticleCardLink>
            </h3>
            <CompactMeta article={article} />
        </article>
    );
}

/**
 * (A3) News mosaic: wide lead + two dense cover tiles, then a 3-up cover row.
 * Distinct from spotlight (list), dual (horizontal stack), and grid (2-col thumbs).
 */
function MosaicRail({ articles }: { articles: CardArticle[] }) {
    const [lead, second, third, ...bottom] = articles;

    if (!second) {
        return <SpotlightRail articles={articles} />;
    }

    const side = [second, third].filter(Boolean) as CardArticle[];
    const hasBottom = bottom.length > 0;

    return (
        <div>
            <div className="md:grid md:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)] md:items-start">
                <LeadTeaser
                    article={lead}
                    className={cn(
                        'border-b border-white/10 md:border-e md:self-stretch',
                        !hasBottom && 'md:border-b-0',
                    )}
                />
                <div
                    className={cn(
                        'flex flex-col border-b border-white/10',
                        !hasBottom && 'md:border-b-0',
                    )}
                >
                    {side.map((article, index) => (
                        <CoverTile
                            key={article.slug}
                            article={article}
                            dense
                            className={cn(
                                index < side.length - 1 && 'border-b border-white/10',
                            )}
                        />
                    ))}
                </div>
            </div>

            {hasBottom ? (
                <div className="grid sm:grid-cols-3">
                    {bottom.map((article, index) => {
                        const isLast = index === bottom.length - 1;
                        const isLastCol = (index + 1) % 3 === 0;

                        return (
                            <CoverTile
                                key={article.slug}
                                article={article}
                                className={cn(
                                    'border-white/10',
                                    !isLast && 'max-sm:border-b',
                                    !isLastCol && 'sm:border-e',
                                )}
                            />
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

/** (B) Dense 2-column teaser grid with optional thumbnails. */
function GridRail({ articles }: { articles: CardArticle[] }) {
    const lastRowStart =
        articles.length - (articles.length % 2 === 0 ? 2 : 1);

    return (
        <div className="grid sm:grid-cols-2">
            {articles.map((article, index) => {
                const isLast = index === articles.length - 1;
                const inLastRow = index >= lastRowStart;
                const isStartCol = index % 2 === 0;

                return (
                    <article
                        key={article.slug}
                        className={cn(
                            'group flex items-start gap-3 border-white/10 p-4 md:gap-4 md:p-5',
                            !isLast && 'max-sm:border-b',
                            !inLastRow && 'sm:border-b',
                            isStartCol && 'sm:border-e',
                        )}
                    >
                        {article.cover ? (
                            <CardThumbLink
                                article={article}
                                className="image-frame shrink-0 border border-white/10"
                            >
                                <SoftImage
                                    src={article.cover}
                                    alt=""
                                    width={112}
                                    height={84}
                                    sizes="(min-width: 768px) 112px, 80px"
                                    className="h-16 w-20 object-cover md:h-[84px] md:w-[112px]"
                                    loading="lazy"
                                />
                            </CardThumbLink>
                        ) : null}
                        <div className="min-w-0 flex-1 self-center">
                            <ArticleTrailer trailer={article.trailer} />
                            <h3
                                className={cn(
                                    'title-two-line text-base font-normal leading-snug tracking-tight md:text-lg',
                                    article.trailer && 'mt-1',
                                )}
                            >
                                <ArticleCardLink
                                    article={article}
                                    className="text-white hover:text-white/85"
                                >
                                    {article.title}
                                </ArticleCardLink>
                            </h3>
                            <CompactMeta article={article} />
                        </div>
                    </article>
                );
            })}
        </div>
    );
}

/**
 * (D) Transport board: full-width cover banner for the lead,
 * then an equal-column strip for the rest - unlike grid/mosaic/spotlight.
 */
function BoardRail({ articles }: { articles: CardArticle[] }) {
    const [lead, ...restAll] = articles;
    const rest = restAll.slice(0, 2);
    const hasCover = cardCoverSrcs(lead).length > 0;

    return (
        <div>
            <article className="group border-b border-white/10">
                {hasCover ? (
                    <LinkedCardCover
                        article={lead}
                        className="border-b border-white/10"
                        width={1600}
                        height={686}
                        sizes="100vw"
                        imageClassName="aspect-[21/9] w-full md:aspect-[2.6/1]"
                        loading="lazy"
                    />
                ) : (
                    <div
                        className="aspect-[21/9] border-b border-white/10 bg-white/[0.03] md:aspect-[2.6/1]"
                        aria-hidden
                    />
                )}
                <div className="px-4 py-4 md:px-5 md:py-5">
                    <ArticleTrailer trailer={lead.trailer} />
                    {lead.isLiveReport && (
                        <p className={cn('text-sm text-brand-accent', lead.trailer && 'mt-1')}>
                            שידור חי
                        </p>
                    )}
                    <h3
                        className={cn(
                            'title-two-line text-xl font-normal tracking-tight md:text-2xl lg:text-[1.65rem]',
                            (lead.trailer || lead.isLiveReport) && 'mt-1',
                        )}
                    >
                        <ArticleCardLink
                            article={lead}
                            className="text-white hover:text-white/85"
                        >
                            {lead.title}
                        </ArticleCardLink>
                    </h3>
                    {lead.description && (
                        <ArticleCardLink
                            article={lead}
                            className="mt-2 line-clamp-2 block max-w-3xl text-sm leading-relaxed text-white/55 hover:text-white/70 md:line-clamp-3"
                        >
                            {lead.description}
                        </ArticleCardLink>
                    )}
                    <CompactMeta article={lead} />
                </div>
            </article>

            {rest.length > 0 ? (
                <ol className="grid sm:grid-cols-2">
                    {rest.map((article, index) => {
                        const isLast = index === rest.length - 1;

                        return (
                            <li
                                key={article.slug}
                                className={cn(
                                    'group border-white/10 p-4',
                                    !isLast && 'max-sm:border-b sm:border-e',
                                )}
                            >
                                <ArticleTrailer trailer={article.trailer} />
                                <h3
                                    className={cn(
                                        'title-two-line text-sm font-normal leading-snug tracking-tight md:text-base',
                                        article.trailer && 'mt-1',
                                    )}
                                >
                                    <ArticleCardLink
                                        article={article}
                                        className="text-white hover:text-white/85"
                                    >
                                        {article.title}
                                    </ArticleCardLink>
                                </h3>
                                <CompactMeta article={article} />
                            </li>
                        );
                    })}
                </ol>
            ) : null}
        </div>
    );
}

/**
 * Crime pair: two large vertical cards side by side, optional third row.
 * Homepage feeds this rail at most 3 articles.
 */
function DocketRail({ articles }: { articles: CardArticle[] }) {
    const pair = articles.slice(0, 2);
    const third = articles[2];

    if (pair.length < 2) {
        return <SpotlightRail articles={articles} />;
    }

    return (
        <div>
            <div className="grid md:grid-cols-2">
                {pair.map((article, index) => {
                    const hasCover = cardCoverSrcs(article).length > 0;

                    return (
                        <article
                            key={article.slug}
                            className={cn(
                                'group border-white/10 p-4 md:p-6',
                                'border-b',
                                index === 0 && 'md:border-e',
                            )}
                        >
                            {hasCover ? (
                                <LinkedCardCover
                                    article={article}
                                    className="mb-4 border border-white/10 md:mb-5"
                                    imageClassName="aspect-[16/10] w-full"
                                    sizes="(min-width: 768px) 50vw, 100vw"
                                    loading="lazy"
                                />
                            ) : (
                                <div
                                    className="mb-4 aspect-[16/10] border border-white/10 bg-white/[0.03] md:mb-5"
                                    aria-hidden
                                />
                            )}
                            <ArticleTrailer trailer={article.trailer} />
                            {article.isLiveReport && (
                                <p className={cn('text-sm text-brand-accent', article.trailer && 'mt-1')}>
                                    שידור חי
                                </p>
                            )}
                            <h3
                                className={cn(
                                    'title-two-line text-xl font-normal tracking-tight md:text-2xl lg:text-[1.75rem]',
                                    (article.trailer || article.isLiveReport) && 'mt-1',
                                )}
                            >
                                <ArticleCardLink
                                    article={article}
                                    className="text-white hover:text-white/85"
                                >
                                    {article.title}
                                </ArticleCardLink>
                            </h3>
                            {article.description && (
                                <ArticleCardLink
                                    article={article}
                                    className="mt-2 line-clamp-3 block text-sm leading-relaxed text-white/55 hover:text-white/70 md:text-[15px]"
                                >
                                    {article.description}
                                </ArticleCardLink>
                            )}
                            <CompactMeta article={article} />
                        </article>
                    );
                })}
            </div>

            {third ? (
                <HorizontalLeadTeaser article={third} />
            ) : null}
        </div>
    );
}

/**
 * (E) Feature lead: edge-to-edge cinematic cover for the main story,
 * then a compact headline stack for the rest.
 */
function FeatureRail({ articles }: { articles: CardArticle[] }) {
    const [lead, ...rest] = articles;
    const hasCover = cardCoverSrcs(lead).length > 0;

    return (
        <div>
            <article className="group border-b border-white/10">
                {hasCover ? (
                    <LinkedCardCover
                        article={lead}
                        className="border-b border-white/10"
                        width={1600}
                        height={900}
                        sizes="100vw"
                        imageClassName="aspect-[16/9] w-full max-h-[min(72vh,44rem)]"
                        loading="lazy"
                    />
                ) : (
                    <div
                        className="aspect-[16/9] max-h-[min(72vh,44rem)] w-full border-b border-white/10 bg-white/[0.03]"
                        aria-hidden
                    />
                )}
                <div className="px-4 py-4 md:px-6 md:py-5">
                    <ArticleTrailer trailer={lead.trailer} />
                    {lead.isLiveReport && (
                        <p className={cn('text-sm text-brand-accent', lead.trailer && 'mt-1')}>
                            שידור חי
                        </p>
                    )}
                    <h3
                        className={cn(
                            'max-w-4xl text-2xl font-normal tracking-tight md:text-3xl lg:text-[2rem]',
                            (lead.trailer || lead.isLiveReport) && 'mt-1',
                        )}
                    >
                        <ArticleCardLink
                            article={lead}
                            className="text-white hover:text-white/85"
                        >
                            {lead.title}
                        </ArticleCardLink>
                    </h3>
                    {lead.description && (
                        <ArticleCardLink
                            article={lead}
                            className="mt-3 line-clamp-3 block max-w-3xl text-sm leading-relaxed text-white/55 hover:text-white/70 md:text-base md:leading-7"
                        >
                            {lead.description}
                        </ArticleCardLink>
                    )}
                    <CompactMeta article={lead} />
                </div>
            </article>

            {rest.length > 0 ? <CompactTeaserList articles={rest} /> : null}
        </div>
    );
}

/** (C) Horizontal rows with thumbnails. */
function ThumbsRail({ articles }: { articles: CardArticle[] }) {
    return (
        <ul>
            {articles.map((article, index) => (
                <li
                    key={article.slug}
                    className={cn(
                        'group flex items-start gap-3 px-4 py-3 md:gap-4 md:px-5 md:py-5',
                        index < articles.length - 1 && 'border-b border-white/10',
                    )}
                >
                    {article.cover ? (
                        <CardThumbLink
                            article={article}
                            className="image-frame shrink-0 border border-white/10"
                        >
                            <SoftImage
                                src={article.cover}
                                alt=""
                                width={128}
                                height={96}
                                sizes="(min-width: 768px) 128px, 80px"
                                className="h-16 w-20 object-cover md:h-[96px] md:w-[128px]"
                                loading="lazy"
                            />
                        </CardThumbLink>
                    ) : (
                        <div
                            className="h-16 w-20 shrink-0 border border-white/10 bg-white/[0.03] md:h-[96px] md:w-[128px]"
                            aria-hidden
                        />
                    )}
                    <div className="min-w-0 flex-1 self-center">
                        <ArticleTrailer trailer={article.trailer} />
                        <h3
                            className={cn(
                                'title-two-line text-base font-normal leading-snug tracking-tight md:text-lg',
                                article.trailer && 'mt-1',
                            )}
                        >
                            <ArticleCardLink
                                article={article}
                                className="text-white hover:text-white/85"
                            >
                                {article.title}
                            </ArticleCardLink>
                        </h3>
                        <CompactMeta article={article} />
                    </div>
                </li>
            ))}
        </ul>
    );
}

/**
 * Dense homepage category section with layout variants.
 * Category / author listing pages still use {@link SectionArticles}.
 */
export function CategoryRail({
    title,
    label,
    articles,
    categoryKey,
    variant = 'spotlight',
}: {
    title: string;
    label?: string;
    articles: CardArticle[];
    categoryKey?: string;
    variant?: CategoryRailVariant;
}) {
    if (articles.length === 0) return null;

    // Single-item sections: prefer thumbs so we don't leave an empty list column.
    const resolved =
        articles.length === 1 &&
        (variant === 'spotlight' ||
            variant === 'dual' ||
            variant === 'mosaic' ||
            variant === 'board' ||
            variant === 'docket')
            ? 'thumbs'
            : variant;

    return (
        <ShowCategoryInMetaContext.Provider value={!categoryKey}>
            <section>
                <SectionHeader title={title} label={label} categoryKey={categoryKey} />
                {resolved === 'spotlight' && <SpotlightRail articles={articles} />}
                {resolved === 'dual' && <DualSpotlightRail articles={articles} />}
                {resolved === 'mosaic' && <MosaicRail articles={articles} />}
                {resolved === 'grid' && <GridRail articles={articles} />}
                {resolved === 'thumbs' && <ThumbsRail articles={articles} />}
                {resolved === 'board' && <BoardRail articles={articles} />}
                {resolved === 'feature' && <FeatureRail articles={articles} />}
                {resolved === 'docket' && <DocketRail articles={articles} />}
            </section>
        </ShowCategoryInMetaContext.Provider>
    );
}

export function SectionArticles({
    title,
    label,
    articles,
}: {
    title: string;
    label?: string;
    articles: CardArticle[];
}) {
    if (articles.length === 0) return null;

    return (
        <div>
            <SectionHeader title={title} label={label} />
            <div>
                {articles.map((article, index) => (
                    <ArticleRow
                        key={article.slug}
                        article={article}
                        showBorder={index < articles.length - 1}
                    />
                ))}
            </div>
        </div>
    );
}

export function RelatedArticles({
    articles,
    title = 'עוד כתבות',
}: {
    articles: CardArticle[];
    title?: string;
}) {
    if (articles.length === 0) return null;

    return (
        <div>
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-3">
                <p className="text-sm text-white/55">{title}</p>
                <Link to="/" className="text-sm text-white/55 hover:text-white">
                    לדף הבית
                </Link>
            </div>
            <div className="grid md:grid-cols-3">
                {articles.map((item, index) => {
                    const isLastOverall = index === articles.length - 1;
                    const isLastColumn = index % 3 === 2;
                    const hasItemBelow = index + 3 < articles.length;

                    return (
                        <div
                            key={item.slug}
                            className={cn(
                                'border-white/10 p-6',
                                !isLastOverall && 'max-md:border-b',
                                hasItemBelow && 'md:border-b',
                                !isLastColumn && 'md:border-e',
                            )}
                        >
                            {item.createdAt && (
                                <time
                                    dateTime={item.createdAt}
                                    className="text-sm text-white/55"
                                >
                                    {formatHebrewDate(item.createdAt).split(',')[0]}
                                </time>
                            )}
                            <ArticleTrailer
                                trailer={item.trailer}
                                className={item.createdAt ? 'mt-2' : undefined}
                            />
                            <h2
                                className={cn(
                                    'title-single-line text-base font-normal tracking-tight',
                                    item.trailer ? 'mt-1' : 'mt-3',
                                )}
                            >
                                <ArticleCardLink
                                    article={item}
                                    className="text-white hover:text-white/85"
                                >
                                    {item.title}
                                </ArticleCardLink>
                            </h2>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export type { CardArticle };
